import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Gauge, Phone } from "lucide-react";
import { PrimaryLink } from "./ui/Buttons";
import { PHONE_TEL, PHONE_DISPLAY } from "../lib/businessInfo";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navRef = useRef(null);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const handleNavClick = () => setIsMenuOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target))
        setIsMenuOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/blogs", label: "Blogs" },
    { to: "/contact", label: "Contact" },
    { to: "/about", label: "About" },
  ];

  return (
    <nav
      ref={navRef}
      data-nav-root
      aria-label="Primary navigation"
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-md"
    >
      {/* Utility row — desktop / tablet only to avoid crowding small phones */}
      <div className="hidden border-b border-slate-100 bg-slate-50/90 backdrop-blur-sm md:block">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2 text-xs text-navy-900 sm:text-[13px]">
          <a
            href={PHONE_TEL}
            data-gtm="phone-click"
            className="font-semibold text-navy-900 transition-colors hover:text-teal-600"
          >
            {PHONE_DISPLAY}
          </a>
          <span className="text-slate-600">
            Mon–Fri 8:30 AM–5:30 PM · Closed weekends
          </span>
        </div>
      </div>

      <div className="mx-auto flex h-[76px] max-w-6xl items-center justify-between gap-3 px-4 md:h-[84px]">
        <NavLink
          to="/"
          onClick={handleNavClick}
          aria-label="Advanced Automotive Smog home"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg md:flex-none md:shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40 focus-visible:ring-offset-2"
        >
          <span className="flex shrink-0 text-teal-600" aria-hidden>
            <Gauge className="h-5 w-5 md:h-6 md:w-6" strokeWidth={2} />
          </span>

          <div className="min-w-0 flex flex-col leading-none">
            <span className="text-[14px] font-semibold leading-tight tracking-tight text-navy-900 sm:text-[15px] md:text-[16px]">
              Advanced Automotive Smog
            </span>
            <span className="mt-1 text-[11px] font-normal text-slate-600 md:text-[12px]">
              Temecula, CA
            </span>
          </div>
        </NavLink>

        <div className="hidden items-center gap-7 md:flex">
          <div className="flex items-center gap-7 text-[13px] font-medium tracking-tight text-slate-700">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  [
                    "relative transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:text-teal-700",
                    isActive
                      ? "text-teal-700"
                      : "text-slate-700 hover:text-navy-900",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <span className="absolute -bottom-2 left-0 right-0 mx-auto h-0.5 w-5 rounded-full bg-teal-600" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <PrimaryLink
            href={PHONE_TEL}
            data-gtm="phone-click"
            className="px-5 py-2.5 text-sm"
          >
            Call Now <span aria-hidden className="opacity-90">›</span>
          </PrimaryLink>
        </div>

        <div className="flex shrink-0 items-center gap-2 md:hidden">
          <a
            href={PHONE_TEL}
            data-gtm="phone-click"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border-2 border-teal-600 bg-teal-50 text-teal-700 transition-colors hover:bg-teal-100 touch-manipulation"
            aria-label={`Call ${PHONE_DISPLAY}`}
          >
            <Phone className="h-5 w-5" strokeWidth={2.25} aria-hidden />
          </a>

          <button
            type="button"
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-primary-nav"
            aria-label={isMenuOpen ? "Close navigation" : "Open navigation"}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border-2 border-slate-200 bg-white text-navy-900 transition-colors hover:bg-slate-50 touch-manipulation"
          >
            {isMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 6l12 12M6 18L18 6"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-7 w-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 7h16M4 12h16M4 17h16"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div
        id="mobile-primary-nav"
        className={`md:hidden overflow-hidden border-t border-slate-200 bg-white transition-[max-height,opacity] duration-300 ease-in-out ${
          isMenuOpen ? "max-h-[520px] opacity-100 shadow-lg" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 py-4">
          <ul className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    [
                      "flex items-center justify-between rounded-xl border-2 px-4 py-3.5 transition-colors",
                      isActive
                        ? "border-teal-600 bg-teal-50 text-navy-900"
                        : "border-slate-200 bg-white text-navy-900 hover:border-slate-300 hover:bg-slate-50",
                    ].join(" ")
                  }
                >
                  <span className="text-[15px] font-semibold tracking-wide">
                    {link.label}
                  </span>
                  <span className="text-slate-400">›</span>
                </NavLink>
              </li>
            ))}
          </ul>

          <PrimaryLink
            href={PHONE_TEL}
            data-gtm="phone-click"
            className="mt-3 w-full justify-center px-4 py-3.5 text-sm"
          >
            Call Now <span aria-hidden className="opacity-90">›</span>
          </PrimaryLink>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
